import {differenceInCalendarDays, subDays} from 'date-fns'
import {orderBy, round, sumBy} from 'lodash'
import {DashboardBaseFilterModel} from '@/dashboard/models/DashboardBaseFilterModel'
import {IDateRange} from '@/utils/interfaces/DateRangeInterface'
import {chartColors} from '@/utils/constants/ChartTheme'

export interface IDailyValue {
    date: Date|string|null
    value: number
}

export interface IKpi {
    value: number
    previous: number
    variation: number|null
    trend: number[]
}

export interface IDashboardKpis {
    money: IKpi
    sales: IKpi
    ticket: IKpi
}

export interface IBreakdownEntry {
    id: string|number
    name: string
    color?: string
    list: IDailyValue[]
}

export interface IBreakdownRankItem {
    id: string|number
    name: string
    color: string
    total: number
    share: number
}

export interface IBreakdownSeries {
    id: string|number
    name: string
    color: string
    data: number[]
}

export interface IBreakdown {
    categories: string[]
    series: IBreakdownSeries[]
    ranking: IBreakdownRankItem[]
    total: number
}

/**
 * Intervalo de mesmo tamanho (em dias de calendário) imediatamente anterior ao `start`.
 * Ex.: 10/01 a 24/01 (15 dias) -> 26/12 a 09/01.
 */
export function previousPeriod(range:IDateRange):IDateRange {
    const start = new Date(range.start as Date)
    const end = new Date(range.end as Date)
    const days = periodDays({start, end})

    const previousEnd = subDays(start, 1)
    const previousStart = subDays(previousEnd, days - 1)

    return {
        start: previousStart,
        end: previousEnd,
    }
}

export function periodDays(range:IDateRange):number {
    return differenceInCalendarDays(new Date(range.end as Date), new Date(range.start as Date)) + 1
}

export function cloneFilterWith(filter:DashboardBaseFilterModel, partial:Partial<DashboardBaseFilterModel> = {}):DashboardBaseFilterModel {
    const date = partial.date || filter.date

    return new DashboardBaseFilterModel({
        channelListWrapper: filter.channelListWrapper || [],
        storeListWrapper: filter.storeListWrapper || [],
        ...partial,
        date: {start: date.start, end: date.end},
        dateSubmitted: {start: date.start, end: date.end},
    })
}

export function variation(current:number, previous:number):number|null {
    if (!previous) {
        return null
    }

    return round(((current - previous) / previous) * 100, 1)
}

function sumValues(list:IDailyValue[]):number {
    return sumBy(list || [], (d) => Number(d.value) || 0)
}

function ticketOf(money:number, sales:number):number {
    return sales ? round(money / sales, 2) : 0
}

export function buildKpis(
    moneyNow:IDailyValue[],
    salesNow:IDailyValue[],
    moneyPrev:IDailyValue[],
    salesPrev:IDailyValue[],
):IDashboardKpis {
    const money = round(sumValues(moneyNow), 2)
    const sales = sumValues(salesNow)
    const previousMoney = sumValues(moneyPrev)
    const previousSales = sumValues(salesPrev)

    // Ticket ponderado: receita / pedidos, e não média dos tickets diários.
    const ticket = ticketOf(money, sales)
    const previousTicket = ticketOf(previousMoney, previousSales)

    const salesByDate = new Map((salesNow || []).map((s) => [String(s.date), Number(s.value) || 0]))

    return {
        money: {
            value: money,
            previous: round(previousMoney, 2),
            variation: variation(money, previousMoney),
            trend: (moneyNow || []).map((m) => Number(m.value) || 0),
        },
        sales: {
            value: sales,
            previous: previousSales,
            variation: variation(sales, previousSales),
            trend: (salesNow || []).map((s) => Number(s.value) || 0),
        },
        ticket: {
            value: ticket,
            previous: previousTicket,
            variation: variation(ticket, previousTicket),
            trend: (moneyNow || []).map((m) => ticketOf(Number(m.value) || 0, salesByDate.get(String(m.date)) || 0)),
        },
    }
}

// Cor de marca muito clara e sem saturação (ex.: canal personalizado) some no gráfico.
export function isUsableChartColor(color?:string):boolean {
    if (!color || !/^#[0-9a-f]{6}$/i.test(color)) {
        return false
    }

    const [r, g, b] = [1, 3, 5].map((i) => parseInt(color.substr(i, 2), 16) / 255)
    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const saturation = max === 0 ? 0 : (max - min) / max

    return !(max > 0.85 && saturation < 0.25)
}

/**
 * Cor da marca quando possível; repetida (duas contas do mesmo marketplace) ou
 * inutilizável cai na próxima cor livre da paleta.
 */
export function resolveColors(preferred:Array<string|undefined>):string[] {
    const used = new Set<string>()
    let paletteIndex = 0

    return preferred.map((color) => {
        const key = (color || '').toLowerCase()

        if (isUsableChartColor(color) && !used.has(key)) {
            used.add(key)
            return color as string
        }

        while (paletteIndex < chartColors.length - 1 && used.has(chartColors[paletteIndex].toLowerCase())) {
            paletteIndex++
        }

        const fallback = chartColors[paletteIndex % chartColors.length]
        used.add(fallback.toLowerCase())
        paletteIndex++

        return fallback
    })
}

export function buildBreakdown(entries:IBreakdownEntry[]):IBreakdown {
    const withTotal = (entries || [])
        .map((e) => ({...e, total: sumValues(e.list)}))
        .filter((e) => e.total > 0)

    const sorted = orderBy(withTotal, ['total'], ['desc'])
    const colors = resolveColors(sorted.map((e) => e.color))

    const dateSet = new Set<string>()
    sorted.forEach((e) => e.list.forEach((d) => dateSet.add(String(d.date))))
    const categories = Array.from(dateSet).sort((a, b) => new Date(a).getTime() - new Date(b).getTime())

    const total = sumBy(sorted, 'total')

    const series = sorted.map((e, index) => {
        const byDate = new Map(e.list.map((d) => [String(d.date), Number(d.value) || 0]))

        return {
            id: e.id,
            name: e.name,
            color: colors[index],
            data: categories.map((c) => round(byDate.get(c) || 0, 2)),
        }
    })

    const ranking = sorted.map((e, index) => ({
        id: e.id,
        name: e.name,
        color: colors[index],
        total: round(e.total, 2),
        share: total ? round((e.total / total) * 100, 1) : 0,
    }))

    return {
        categories,
        series,
        ranking,
        total: round(total, 2),
    }
}
